"use client"

import { useLocale } from "next-intl"
import { useCallback } from "react"
import type { Locale } from "./insurance-types"
import de from "@/data/insurance/i18n/de.json"
import en from "@/data/insurance/i18n/en.json"
import fr from "@/data/insurance/i18n/fr.json"

/**
 * Translation hook for the ported insurance coverage checker.
 *
 * The checker's copy uses flat dot-notation keys (e.g. `"gate.success.desc"`
 * AND `"gate.success.desc.known"`) with parent/leaf overlaps that next-intl's
 * nested message model cannot represent — and `{{param}}` mustache
 * interpolation rather than ICU. So the copy is bundled as static JSON here
 * (SSR-rendered, no runtime fetch / no flash — unlike the old Locize layer)
 * and looked up directly, keeping the checker's exact fallback + param
 * semantics. Locale comes from next-intl routing.
 */
const STRINGS: Record<Locale, Record<string, string>> = { de, en, fr }

export function useInsuranceTranslation(): {
  t: (key: string, params?: Record<string, string>) => string
  locale: Locale
} {
  const rawLocale = useLocale()
  const locale: Locale =
    rawLocale === "en" || rawLocale === "fr" ? rawLocale : "de"

  const strings = STRINGS[locale]

  const t = useCallback(
    (key: string, params?: Record<string, string>): string => {
      let text = strings[key]
      if (text === undefined) return key
      if (params) {
        for (const [param, value] of Object.entries(params)) {
          text = text.replace(new RegExp(`\\{\\{${param}\\}\\}`, "g"), value)
        }
      }
      return text
    },
    [strings]
  )

  return { t, locale }
}
